import React, { createContext, useState, useEffect, useContext } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

// Light theme colors
const lightTheme = {
  bg: '#f8f9fa',
  bgSecondary: '#ffffff',
  text: '#212529',
  textSecondary: '#6c757d',
  border: '#dee2e6',
  hover: '#e9ecef',
  accent: '#06B6D4', // Modern teal/cyan
  accentGradient: 'none',
  shadow: '0 6px 12px rgba(0, 0, 0, 0.05)',
};

// Dark theme colors
const darkTheme = {
  bg: '#121212',
  bgSecondary: '#1e1e1e',
  text: '#f8f9fa',
  textSecondary: '#adb5bd',
  border: '#343a40',
  hover: '#2b3035',
  accent: '#22D3EE', // Lighter teal/cyan for dark mode
  accentGradient: 'linear-gradient(135deg, #22D3EE, #06B6D4)',
  shadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
};

// Create theme context
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
});

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Check for saved theme preference or use preferred color scheme
  const getSavedTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // Use preferred color scheme if available
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  };
  
  const [currentTheme, setCurrentTheme] = useState(getSavedTheme);
  
  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    setCurrentTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };
  
  // Set theme on initial load and when theme changes
  useEffect(() => {
    document.body.className = `${currentTheme}-theme`;
  }, [currentTheme]);
  
  // Get the current theme object
  const theme = currentTheme === 'light' ? lightTheme : darkTheme;
  
  return (
    <ThemeContext.Provider value={{ theme: currentTheme, toggleTheme }}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
